import React, { useEffect, useMemo, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { roleLabel } from '../config/roles';

// Admin page tab: email all users, all agents, or a custom pick. One email per person, sent from Kelly
// (system sender) by the server (/api/admin-email-users), which re-checks admin and recipients.
const isAgentRole = (r) => ['agent', 'limited_agent', 'limited agent'].includes(String(r || '').trim().toLowerCase());
const isInactive = (u) => u.disabled === true || u.active === false || /^(disabled|inactive|archived|deleted|removed)$/i.test(String(u.status || '').trim());
const nameOf = (u) => u.displayName || [u.profile && u.profile.firstName, u.profile && u.profile.lastName].filter(Boolean).join(' ') || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '(no name)';
const validEmail = (e) => /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(String(e || '').trim());
const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const previewHtml = (t) => esc(t).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  .split(/\n{2,}/).map((p) => `<p style="margin:0 0 12px">${p.replace(/\n/g, '<br>')}</p>`).join('');

const box = { background: '#fff', color: '#1A1A1A', borderRadius: 12, padding: 20, boxSizing: 'border-box', width: '100%', maxWidth: 760 };
const input = { width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #ccc', fontSize: 16 };
const btn = (primary) => ({ padding: '10px 16px', borderRadius: 8, fontWeight: 700, border: '1px solid #B8962E', background: primary ? '#B8962E' : '#fff', color: primary ? '#fff' : '#1A1A1A', cursor: 'pointer' });

export default function AdminEmailUsers() {
  const { user, userRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState('all');
  const [picked, setPicked] = useState({});
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [step, setStep] = useState('compose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (userRole !== 'Admin') return;
    get(ref(database, 'users')).then((s) => {
      const v = s.val() || {};
      setUsers(Object.entries(v).filter(([, u]) => u && typeof u === 'object').map(([uid, u]) => ({ uid, name: nameOf(u), email: u.email || '', role: u.role || '', inactive: isInactive(u) }))
        .sort((a, b) => a.name.localeCompare(b.name)));
    }).catch(() => setError('Could not load users.'));
  }, [userRole]);

  const recipients = useMemo(() => users.filter((u) => !u.inactive && validEmail(u.email) && (mode === 'all' || (mode === 'agents' && isAgentRole(u.role)) || (mode === 'custom' && picked[u.uid]))), [users, mode, picked]);
  const shown = users.filter((u) => !search.trim() || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(search.trim().toLowerCase()));
  const ready = subject.trim() && body.trim() && recipients.length > 0;

  const send = async () => {
    setBusy(true); setError('');
    try {
      const idToken = await user.getIdToken();
      const r = await fetch('/api/admin-email-users', { method: 'POST', headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, mode, uids: mode === 'custom' ? recipients.map((u) => u.uid) : undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not send.');
      setResult(d); setStep('done');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const reset = () => { setStep('compose'); setResult(null); setSubject(''); setBody(''); setPicked({}); };

  if (userRole !== 'Admin') return null;

  if (step === 'done' && result) return (
    <div style={box}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Sent {result.sentCount} of {result.results.length}</h2>
      {result.results.some((r) => r.testMode) && <p style={{ color: '#8a6d00' }}>Email test mode is on, so these went only to the test inbox.</p>}
      <ul style={{ paddingLeft: 18, lineHeight: 1.6, wordBreak: 'break-word' }}>
        {result.results.map((r) => <li key={r.uid}>{r.name || r.email}: {r.sent ? 'sent' : `failed (${r.error})`}</li>)}
      </ul>
      {result.skipped && result.skipped.length > 0 && <p>Skipped {result.skipped.length} (no email or inactive).</p>}
      <button type="button" style={btn(true)} onClick={reset}>Write another</button>
    </div>
  );

  return (
    <div style={box}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Email users</h2>
      {step === 'compose' ? (<>
        <p style={{ lineHeight: 1.5, marginTop: 0 }}>Each person gets their own email from you. Nobody sees the other recipients. Use **bold** or *italic* for simple formatting.</p>
        <label style={{ fontWeight: 600 }}>Send to</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0 12px' }}>
          {[['all', 'All users'], ['agents', 'All agents'], ['custom', 'Custom pick']].map(([k, t]) => (
            <button key={k} type="button" onClick={() => setMode(k)} style={btn(mode === k)}>{t}</button>))}
        </div>
        {mode === 'custom' && (
          <div style={{ marginBottom: 12 }}>
            <input style={input} placeholder="Search users" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, marginTop: 6 }}>
              {shown.map((u) => {
                const can = !u.inactive && validEmail(u.email);
                return (
                  <label key={u.uid} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #f2f2f2', opacity: can ? 1 : 0.5 }}>
                    <input type="checkbox" disabled={!can} checked={!!picked[u.uid]} onChange={(e) => setPicked({ ...picked, [u.uid]: e.target.checked })} />
                    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{u.name} <span style={{ color: '#777', fontSize: 13 }}>{roleLabel ? roleLabel(u.role) : u.role}{!can ? (u.inactive ? ' · inactive' : ' · no email') : ''}</span></span>
                  </label>);
              })}
            </div>
          </div>
        )}
        <label style={{ fontWeight: 600 }}>Subject</label>
        <input style={{ ...input, margin: '6px 0 12px' }} value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} />
        <label style={{ fontWeight: 600 }}>Message</label>
        <textarea style={{ ...input, minHeight: 180, margin: '6px 0 12px', fontFamily: 'inherit' }} value={body} onChange={(e) => setBody(e.target.value)} />
        <p style={{ fontWeight: 600 }}>{recipients.length} recipient{recipients.length === 1 ? '' : 's'}</p>
        <button type="button" disabled={!ready} style={{ ...btn(true), opacity: ready ? 1 : 0.5 }} onClick={() => setStep('confirm')}>Preview</button>
      </>) : (<>
        <p style={{ marginTop: 0 }}><strong>Subject:</strong> {subject}</p>
        <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, marginBottom: 12, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: previewHtml(body) }} />
        <p><strong>Going to {recipients.length}:</strong> <span style={{ wordBreak: 'break-word' }}>{recipients.map((u) => u.name).join(', ')}</span></p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" disabled={busy} style={btn(true)} onClick={send}>{busy ? 'Sending…' : `Yes, send to ${recipients.length}`}</button>
          <button type="button" disabled={busy} style={btn(false)} onClick={() => setStep('compose')}>Back to edit</button>
        </div>
      </>)}
      {error && <p style={{ color: '#b00020' }}>{error}</p>}
    </div>
  );
}
